import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PrimaryButton } from '@/components/brand/Buttons';
import SectionHeading from '@/components/brand/SectionHeading';
import SpotlightCard from '@/components/brand/SpotlightCard';
import { fadeUp, staggerParent, viewportOnce } from '@/lib/motion';
import { HOME } from '@/constants/testIds';
import { resolveIcon } from '@/lib/iconMap';

export default function ExploreServices({ data, items }) {
  if (!data?.title && !items?.length) return null;
  const title = data?.title;
  const subtitle = data?.subtitle;
  const footerLabel = data?.footer_label;
  const extraServices = data?.extra_services || [];
  const services = (items || []).map((s) => ({
    ...s,
    icon: resolveIcon(s.icon, ArrowRight),
  }));

  return (
    <section id="services" className="py-16 bg-white">
      <div className="container mx-auto">
        {/* Rounded panel */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="rounded-[28px] bg-gradient-to-b from-sky-100 to-sky-200/60 border border-hairline shadow-soft p-8 lg:p-12 relative overflow-hidden"
        >
          <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#2BA8E0]/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#E8C25A]/10 blur-3xl pointer-events-none" />

          <SectionHeading
            title={title}
            subtitle={subtitle}
            align="center"
            className="relative"
          />

          <motion.div
            data-testid={HOME.servicesGrid}
            variants={staggerParent(0.07)}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="relative mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {services.map((s) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.title}
                  variants={fadeUp}
                  whileHover={{ y: -6 }}
                  transition={{ type: 'spring', stiffness: 280, damping: 22 }}
                  className="h-full"
                >
                  <SpotlightCard className="h-full">
                    <div className="relative p-6 h-full flex flex-col">
                      {/* Navy square icon */}
                      <div className="h-12 w-12 rounded-xl bg-navy-deep inline-flex items-center justify-center text-white shadow-card group-hover:bg-gold-gradient transition-colors duration-300">
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="mt-5 text-lg font-semibold text-navy">{s.title}</h3>
                      <p className="mt-2 text-sm text-slatebody leading-relaxed flex-1">{s.desc}</p>
                      {/* Per-card Explore More link */}
                      <Link
                        to={s.link || '/services'}
                        className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#C9962E] hover:text-[#a07020] transition-colors group/link"
                      >
                        <span>Explore More</span>
                        <motion.span
                          className="inline-flex"
                          initial={false}
                          whileHover={{ x: 3 }}
                          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                        >
                          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover/link:translate-x-1" />
                        </motion.span>
                      </Link>
                    </div>
                  </SpotlightCard>
                </motion.div>
              );
            })}
          </motion.div>
        </motion.div>

        {/* Extra services pill strip */}
        <div className="mt-8 flex flex-col items-center gap-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-navy/40">{footerLabel}</p>
          <div className="flex flex-wrap justify-center gap-2.5">
            {extraServices.map((svc, i) => {
              const Icon = svc.icon || Globe;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={viewportOnce}
                  transition={{ delay: i * 0.07, duration: 0.4 }}
                  whileHover={{ y: -3, scale: 1.05, boxShadow: '0 8px 20px rgba(201,150,46,0.2)' }}
                  style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-hairline cursor-default"
                >
                  <span className="h-5 w-5 rounded-full bg-navy-deep inline-flex items-center justify-center">
                    <Icon className="h-3 w-3 text-white" />
                  </span>
                  <span className="text-xs font-semibold text-navy">{svc.label || svc}</span>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Centered Explore More button */}
        <div className="flex justify-center mt-8">
          <Link to="/services">
            <PrimaryButton icon={<ChevronDown className="h-4 w-4" />}>
              View All Services
            </PrimaryButton>
          </Link>
        </div>
      </div>
    </section>
  );
}
